import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Leaf, ArrowRight, RefreshCcw, Sparkles, ExternalLink } from "lucide-react";
import AdSlot from "./AdSlot";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const quickStartExamples = [
  "E-waste",
  "Fruit waste",
  "Cooking oil",
  "Textile scraps",
  "Rice husk",
  "Plastic"
];

const loadingMessages = [
  "Scanning global research...",
  "Analyzing organic structures...",
  "Finding the Kaagazz Connection..."
];

const WasteIntelligenceEngine = () => {
  const [wasteInput, setWasteInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const handleAnalyze = async () => {
    if (!wasteInput.trim()) return;

    setIsLoading(true);
    setError(null);
    setResults(null);
    setLoadingMessageIndex(0);

    const interval = setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 2000);

    try {
      const response = await axios.post(`${API}/analyze-waste`, {
        waste_type: wasteInput
      });
      setResults(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to analyze waste. Please try again.");
    } finally {
      clearInterval(interval);
      setIsLoading(false);
    }
  };

  const handleChipClick = (example) => {
    setWasteInput(example);
  };

  const handleReset = () => {
    setWasteInput("");
    setResults(null);
    setError(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleAnalyze();
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f6f0]">
      {/* Top Banner Ad */}
      <div className="w-full bg-[#FDFBF7] border-b border-[#E6E2D6] py-4 flex justify-center sticky top-0 z-50">
        <AdSlot 
          slot="1111111111"
          format="horizontal"
          style={{ minHeight: '90px', maxWidth: '970px', width: '100%' }}
          className="rounded-lg overflow-hidden"
        />
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* Left Sidebar Ad - Hidden on mobile */}
        <div className="hidden lg:block lg:w-48 xl:w-64 bg-[#f8f6f0] p-4 sticky top-24 self-start">
          <div className="bg-[#FDFBF7] rounded-2xl border border-[#E6E2D6] p-4 flex items-center justify-center">
            <AdSlot 
              slot="2222222222"
              format="vertical"
              style={{ minHeight: '600px', width: '160px' }}
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center py-12 md:py-20 px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-5xl mx-auto"
          >
        {/* Hero Section */}
        <div className="mb-12 md:mb-16">
          <div className="flex items-center justify-between mb-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="flex items-center gap-3"
            >
              <Leaf className="w-8 h-8 text-[#2E4E3F]" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#B4654A]">Powered by Kaagazz</span>
            </motion.div>

            <motion.a
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              href="https://ecokaagazz.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2 bg-[#2E4E3F] hover:bg-[#1E2B22] text-[#FDFBF7] px-6 py-3 rounded-full font-medium transition-all duration-300 shadow-[0_4px_12px_rgb(46,78,63,0.3)] hover:shadow-[0_6px_20px_rgb(46,78,63,0.4)] hover:-translate-y-0.5"
              data-testid="visit-kaagazz-button"
            >
              <span className="text-sm md:text-base">Visit Kaagazz</span>
              <ExternalLink className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </motion.a>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-5xl md:text-7xl font-bold tracking-tight text-[#1E2B22] leading-none mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Waste Intelligence
            <br />
            Engine
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-base md:text-lg text-[#4A5E51] leading-relaxed max-w-2xl mb-12"
          >
            Discover what your waste is worth. Type any material you discard, and we'll show you
            research-backed innovations transforming it into value.
          </motion.p>

          {/* Input Area */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-8"
          >
            <input
              data-testid="waste-input"
              type="text"
              value={wasteInput}
              onChange={(e) => setWasteInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="What waste do you generate?"
              className="w-full text-3xl md:text-5xl text-[#1E2B22] bg-transparent border-b-2 border-[#E6E2D6] focus:border-[#2E4E3F] transition-colors duration-300 pb-4 outline-none placeholder:text-[#A1B0A6]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            />
          </motion.div>

          {/* Quick Start Chips */}
          {!results && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap gap-3 mb-8"
            >
              {quickStartExamples.map((example, idx) => (
                <button
                  key={idx}
                  data-testid={`chip-${example.toLowerCase().replace(' ', '-')}`}
                  onClick={() => handleChipClick(example)}
                  className="bg-[#2E4E3F]/[0.08] hover:bg-[#2E4E3F]/[0.15] text-[#2E4E3F] text-sm md:text-base px-5 py-2 rounded-full cursor-pointer transition-all duration-300"
                >
                  {example}
                </button>
              ))}
            </motion.div>
          )}

          {/* Analyze Button */}
          {!results && (
            <motion.button
              data-testid="analyze-button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              onClick={handleAnalyze}
              disabled={!wasteInput.trim() || isLoading}
              className="group flex items-center gap-3 bg-[#2E4E3F] hover:bg-[#1E2B22] disabled:bg-[#A1B0A6] text-[#FDFBF7] px-8 py-4 rounded-full font-medium transition-all duration-300 disabled:cursor-not-allowed"
            >
              <span>Discover Transformations</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </motion.button>
          )}
        </div>

        {/* Value Proposition Section - Added for AdSense content requirements */}
        {!results && !isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.75 }}
            className="mb-12 grid md:grid-cols-3 gap-8"
          >
            <div className="bg-[#FDFBF7] p-8 rounded-2xl border border-[#E6E2D6]">
              <div className="w-12 h-12 bg-[#2E4E3F]/10 rounded-full flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-[#2E4E3F]" />
              </div>
              <h3 className="text-xl font-semibold text-[#2E4E3F] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Research-Backed Ideas
              </h3>
              <p className="text-[#4A5E51] leading-relaxed">
                Get 4-6 transformation ideas backed by real research papers, startups, and innovations from around the world. Every suggestion includes credible sources and citations.
              </p>
            </div>

            <div className="bg-[#FDFBF7] p-8 rounded-2xl border border-[#E6E2D6]">
              <div className="w-12 h-12 bg-[#B4654A]/10 rounded-full flex items-center justify-center mb-4">
                <Leaf className="w-6 h-6 text-[#B4654A]" />
              </div>
              <h3 className="text-xl font-semibold text-[#2E4E3F] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Circular Economy
              </h3>
              <p className="text-[#4A5E51] leading-relaxed">
                Discover how your waste can become valuable raw materials. From agricultural residues to e-waste, we show you proven pathways to circular economy solutions.
              </p>
            </div>

            <div className="bg-[#FDFBF7] p-8 rounded-2xl border border-[#E6E2D6]">
              <div className="w-12 h-12 bg-[#2E4E3F]/10 rounded-full flex items-center justify-center mb-4">
                <ArrowRight className="w-6 h-6 text-[#2E4E3F]" />
              </div>
              <h3 className="text-xl font-semibold text-[#2E4E3F] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Partnership Opportunities
              </h3>
              <p className="text-[#4A5E51] leading-relaxed">
                Each analysis includes "The Kaagazz Connection" - exploring how your waste could partner with eco-friendly manufacturers like Kaagazz for mutual benefit.
              </p>
            </div>
          </motion.div>
        )}

        {/* Ad Slot 1: Horizontal Banner below Hero */}
        {!results && !isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mb-12 flex justify-center"
          >
            <AdSlot 
              slot="1234567890"
              format="horizontal"
              style={{ minHeight: '90px', maxWidth: '728px', width: '100%' }}
              className="rounded-lg overflow-hidden"
            />
          </motion.div>
        )}

        {/* Loading State */}
        <AnimatePresence>
          {isLoading && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="flex flex-col items-center justify-center py-20"
            >
              <Sparkles className="w-12 h-12 text-[#2E4E3F] mb-6 animate-pulse" />
              <motion.p
                key={loadingMessageIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="text-xl md:text-2xl text-[#4A5E51] font-medium"
                style={{ fontFamily: "'Figtree', sans-serif" }}
              >
                {loadingMessages[loadingMessageIndex]}
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error State */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8"
          >
            <p className="text-red-800">{error}</p>
          </motion.div>
        )}

        {/* Results */}
        {results && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* Reset Button */}
            <motion.button
              data-testid="reset-button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onClick={handleReset}
              className="flex items-center gap-2 text-[#2E4E3F] hover:text-[#1E2B22] mb-8 transition-colors duration-300"
            >
              <RefreshCcw className="w-4 h-4" />
              <span className="font-medium">Analyze another waste type</span>
            </motion.button>

            {/* Ideas Grid */}
            <motion.div
              variants={{
                hidden: { opacity: 0 },
                show: {
                  opacity: 1,
                  transition: {
                    delayChildren: 0.2,
                    staggerChildren: 0.1
                  }
                }
              }}
              initial="hidden"
              animate="show"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12"
            >
              {results.ideas.map((idea, idx) => (
                <>
                  <motion.div
                    key={idx}
                    data-testid={`idea-card-${idx}`}
                    variants={{
                      hidden: { opacity: 0, y: 20 },
                      show: { opacity: 1, y: 0 }
                    }}
                    className="bg-[#FDFBF7] p-8 rounded-2xl border border-[#E6E2D6] shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-300 flex flex-col gap-4"
                  >
                    <h3
                      className="text-xl md:text-2xl font-medium text-[#2E4E3F] leading-snug"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {idea.title}
                    </h3>

                    <p className="text-base text-[#4A5E51] leading-relaxed">
                      {idea.description}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {idea.categories.map((category, catIdx) => (
                        <span
                          key={catIdx}
                          className="text-xs font-bold uppercase tracking-wider px-3 py-1 bg-[#f8f6f0] border border-[#E6E2D6] text-[#4A5E51] rounded-md"
                        >
                          {category}
                        </span>
                      ))}
                    </div>

                    <a
                      href={idea.source_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-[#B4654A] hover:text-[#2E4E3F] transition-colors duration-300 mt-auto"
                    >
                      <span className="font-medium">{idea.source_name}</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </motion.div>
                  
                  {/* Ad Slot 3: In Results Grid (after 2nd card) */}
                  {idx === 1 && (
                    <motion.div
                      key={`ad-${idx}`}
                      variants={{
                        hidden: { opacity: 0, y: 20 },
                        show: { opacity: 1, y: 0 }
                      }}
                      className="bg-[#FDFBF7] p-4 rounded-2xl border border-[#E6E2D6] shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex items-center justify-center"
                    >
                      <AdSlot 
                        slot="9876543210"
                        format="rectangle"
                        style={{ minHeight: '250px', width: '300px' }}
                      />
                    </motion.div>
                  )}
                </>
              ))}
            </motion.div>

            {/* Ad Slot 2: Display Ad before Kaagazz Connection */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mb-12 flex justify-center"
            >
              <div className="bg-[#FDFBF7] p-4 rounded-2xl border border-[#E6E2D6] shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <AdSlot 
                  slot="1122334455"
                  format="rectangle"
                  style={{ minHeight: '250px', width: '300px' }}
                />
              </div>
            </motion.div>

            {/* Kaagazz Connection */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              data-testid="kaagazz-connection"
              className="bg-[#2E4E3F] text-[#FDFBF7] p-8 md:p-12 rounded-3xl flex flex-col md:flex-row gap-8 items-start relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#1E2B22] rounded-full blur-3xl opacity-20 -mr-32 -mt-32"></div>
              
              <div className="flex-shrink-0 relative z-10">
                <Leaf className="w-12 h-12 md:w-16 md:h-16" />
              </div>

              <div className="relative z-10">
                <h2
                  className="text-3xl md:text-4xl font-semibold tracking-tight mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  The Kaagazz Connection
                </h2>
                <p className="text-base md:text-lg leading-relaxed opacity-90">
                  {results.kaagazz_connection}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </motion.div>
    </div>

    {/* Right Sidebar Ad - Hidden on mobile */}
    <div className="hidden lg:block lg:w-48 xl:w-64 bg-[#f8f6f0] p-4 sticky top-24 self-start">
      <div className="bg-[#FDFBF7] rounded-2xl border border-[#E6E2D6] p-4 flex items-center justify-center">
        <AdSlot 
          slot="3333333333"
          format="vertical"
          style={{ minHeight: '600px', width: '160px' }}
        />
      </div>
    </div>
  </div>

  {/* Bottom Banner Ad */}
  <div className="w-full bg-[#FDFBF7] border-t border-[#E6E2D6] py-4 flex justify-center mt-8">
    <AdSlot 
      slot="4444444444"
      format="horizontal"
      style={{ minHeight: '90px', maxWidth: '970px', width: '100%' }}
      className="rounded-lg overflow-hidden"
    />
  </div>

  {/* Footer with Links */}
  <footer className="w-full bg-[#2E4E3F] text-[#FDFBF7] py-8 px-6">
    <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
      <div className="flex items-center gap-2">
        <Leaf className="w-5 h-5" />
        <span className="text-sm">© {new Date().getFullYear()} Waste Intelligence Engine</span>
      </div>
      
      <div className="flex flex-wrap gap-6 text-sm">
        <Link to="/how-it-works" className="hover:text-[#B4654A] transition-colors">
          How It Works
        </Link>
        <Link to="/about" className="hover:text-[#B4654A] transition-colors">
          About
        </Link>
        <Link to="/privacy-policy" className="hover:text-[#B4654A] transition-colors">
          Privacy Policy
        </Link>
        <a 
          href="https://ecokaagazz.com" 
          target="_blank" 
          rel="noopener noreferrer"
          className="hover:text-[#B4654A] transition-colors flex items-center gap-1"
        >
          Powered by Kaagazz
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  </footer>
</div>
  );
};

export default WasteIntelligenceEngine;